import { useEffect, useRef } from "react";
const LANG_NAME = { hpgl: "HP-GL", gpgl: "GP-GL", dmpl: "DMPL" };
import { toast } from "sonner";
import { makePoller } from "../lib/monitor";
import { useWorkspace } from "../store/WorkspaceContext";
import { api } from "../lib/api";
import { bridgeStatus, DEFAULT_CONNECTION, targetConfigured, applySuggested } from "../lib/transport";
import { plotterConnected, cutterDevices, detectMachine, rawQueueFor } from "../engine/detect";

const targetKey = (t) => (t ? `${t.type}:${t.printerName || t.serialPort || t.host || ""}` : "");
const savedMatch = (p, d) => p.type === d.type && (d.type === "printer" ? p.printerName === d.printerName : p.serialPort === d.serialPort);

export function useBridgeAutodetect() {
  const { state, dispatch } = useWorkspace();
  const ref = useRef(state);
  ref.current = state;
  const lastEvent = useRef(null);
  const lastReset = useRef(null);
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      const cur = ref.current;
      const c = { ...DEFAULT_CONNECTION, ...cur.connection };
      let st = null, failed = false;
      try { st = await bridgeStatus(c.shopCode); } catch { st = null; failed = true; }
      if (stop) return;
      // the server paired an unclaimed connector on this shop's network with the account — switch to it, no steps needed
      if (st?.adopted && st.shop_code && st.shop_code !== c.shopCode) {
        const next = { ...c, shopCode: st.shop_code, transport: "bridge", pinned: false };
        localStorage.setItem("xpattern.connection", JSON.stringify(next));
        dispatch({ type: "SET", patch: { connection: next } });
        toast.success(`Plotter connector found on ${st.hostname || "this shop's computer"} — connected automatically`);
        return;
      }
      // owner-sent notice (Fleet → Remote fix → Message / Reset plotter selection): persists on screen until dismissed
      const notice = st?.notice || null;
      if ((notice?.id || null) !== (cur.ownerNotice?.id || null)) {
        if (notice?.kind === "reset_plotter" && notice.id !== lastReset.current) {
          lastReset.current = notice.id;
          const next = { ...c, pinned: false, machinePinned: false, transport: c.transport === "bridge" ? "download" : c.transport };
          localStorage.setItem("xpattern.connection", JSON.stringify(next));
          dispatch({ type: "SET", patch: { connection: next, machinePinned: false } });
        }
        dispatch({ type: "SET", patch: { ownerNotice: notice } });
      }
      if (!st?.online) st = null;
      dispatch({ type: "SET", patch: { bridge: st } });
      // connector-side events (e.g. plotter reset mid-cut → leftover spool data discarded)
      const ev = st?.event;
      if (ev?.id && ev.id !== lastEvent.current) {
        lastEvent.current = ev.id;
        if (Date.now() - new Date(ev.at).getTime() < 120000) toast.warning(ev.message, { duration: 12000 });
      }
      if (failed) throw new Error("bridge status unavailable"); // makePoller backs off while the API is unreachable
      const up = Boolean(st?.ok);
      // A pinned plotter that is no longer present must not block cutting on the plotter that IS plugged in.
      const stale = up && c.pinned && targetConfigured(c) && c.transport === "bridge" && !plotterConnected(c, st) && cutterDevices(st).length > 0;
      // Never adopt a plotter silently for an account that hasn't connected one yet — the customer picks it in Plotter settings.
      // a vendor driver queue (Graphtec CE6000Plus…) re-formats our commands as a print page and the plotter feeds the whole roll:
      // always cut through the raw "Generic / Text Only" queue on the same USB port when there is one — even when the shop picked the driver queue by hand
      const rawSwap = up && c.transport === "bridge" && c.targetType === "printer" && c.printerName ? rawQueueFor(st, c.printerName) : null;
      if (rawSwap) {
        const next = { ...c, printerName: rawSwap, pinned: true };
        localStorage.setItem("xpattern.connection", JSON.stringify(next));
        dispatch({ type: "SET", patch: { connection: next } });
        if (sessionStorage.getItem("xpattern.rawSwapNoted") !== rawSwap) {
          sessionStorage.setItem("xpattern.rawSwapNoted", rawSwap);
          toast.info(`Plotter ready — cutting through "${rawSwap}". "${c.printerName}" is a Windows driver queue: it re-formats cuts and the plotter feeds the roll without cutting.`, { duration: 9000 });
        }
      } else if (up && st.suggested && targetConfigured(c) && (!c.pinned || stale)) {
        const s0 = st.suggested, raw = s0.type === "printer" ? rawQueueFor(st, s0.printerName) : null;
        const s = raw ? { ...s0, printerName: raw } : s0;
        const next = applySuggested(c, s);
        if (next.transport !== c.transport || targetKey(s) !== targetKey({ type: c.targetType, printerName: c.printerName, serialPort: c.serialPort, host: c.host })) {
          localStorage.setItem("xpattern.connection", JSON.stringify(next));
          dispatch({ type: "SET", patch: { connection: next } });
        }
      } else if (up && !plotterConnected(c, st) && cutterDevices(st).length === 1) {
        // exactly one physical plotter on the shop computer: connect to it — no list, no picking, pinned or not
        const d = cutterDevices(st)[0];
        const next = { ...applySuggested(c, d), pinned: true };
        localStorage.setItem("xpattern.connection", JSON.stringify(next));
        dispatch({ type: "SET", patch: { connection: next } });
        if (sessionStorage.getItem("xpattern.autoConnected") !== d.label) {
          sessionStorage.setItem("xpattern.autoConnected", d.label);
          toast.success(`Plotter connected — ${d.label}`, { duration: 6000 });
          // a plotter this account has never saved: the Plotter dialog opens once — it saves the plotter and runs the first-cut language check
          api.plotters().then((r) => {
            if (!(r.plotters || []).some((p) => savedMatch(p, d)) && !ref.current.dialog) dispatch({ type: "SET", patch: { dialog: "settings" } });
          }).catch(() => {});
        }
      }
      if (up && !cur.machinePinned && cur.machines.length) {
        const d = detectMachine(st, cur.machines);
        if (d?.machine && d.machine.id !== cur.machine?.id) {
          dispatch({ type: "SELECT_MACHINE", machine: d.machine, extra: { machineSource: d } }); // keeps the customer's saved speed / force
        } else if (d && d.device !== cur.machineSource?.device) {
          dispatch({ type: "SET", patch: { machineSource: d } });
        }
      } else if (up && cur.machinePinned && cur.machine?.brand === "Generic" && cur.machines.length) {
        // A generic "<language> cutter (auto)" pin (usually from a mis-answered language check) must not override a plotter that
        // names its brand: a Graphtec FC9000 pinned to DMPL fed the whole roll and cut one long line (Ceramic Specialist, 2026-06).
        const d = detectMachine(st, cur.machines);
        if (d?.machine && d.machine.brand !== "Generic" && (d.how === "recognised" || d.how === "usb id") && d.machine.command_format !== cur.machine.command_format) {
          dispatch({ type: "SELECT_MACHINE", machine: d.machine, extra: { machineSource: d, machinePinned: true } });
          toast.message(`Switched to ${d.machine.name}`, { description: `Your plotter identifies itself as a ${d.machine.brand} — it speaks ${LANG_NAME[d.machine.command_format] || d.machine.command_format}, not ${LANG_NAME[cur.machine.command_format] || cur.machine.command_format}.`, duration: 12000 });
        }
      }
    };
    const stopPoll = makePoller(poll, { base: 3000, max: 30000 });
    return () => { stop = true; stopPoll(); };
  }, [state.connection?.shopCode, state.connection?.pinned]); // eslint-disable-line react-hooks/exhaustive-deps
}
